import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import tinyColor from 'tinycolor2';
import Tree, { mutateTree, moveItemOnTree } from '@atlaskit/tree';
import { updateFavorites, updateFavoriteList } from '../../actions/AppActions';
import browserHistory from '../../history';
import { getPageType, whichMenuItem, menuDefs } from '../../util/page';
import { convertToTreeData, convertToSimple, removeFromSimple, renameFromSimple } from './favoriteUtils';
import FavoritesItem from './FavoritesItem';
import NewFolder from './NewFolder';
import { modal } from '../modal/ModalEmitter';
import toastr from '../toastr';
import FileNotFound from '../FileNotFound';

const getIcon = (item, onExpand, onCollapse, icon, color) => {
  if (icon === 'folder') {
    return item.isExpanded ? (
      <div className="folder favorite-icon" onClick={() => onCollapse(item.id)}>
        <i className="fas fa-folder-open" />
      </div>
    ) : (
      <div className="folder favorite-icon" onClick={() => onExpand(item.id)}>
        <i className={`zmdi zmdi-${icon} zmdi-hc-fw zmdi-hc-lg`} />
      </div>
    );
  }

  const style = { backgroundColor: color };
  if (tinyColor(color).getBrightness() === 255) {
    style.color = '#333';
    style.border = '1px solid #333';
  }

  return (
    <div
      className="favorite-icon"
      style={style}
    >
      <i className={`zmdi zmdi-${icon} zmdi-hc-fw zmdi-hc-lg`} />
    </div>
  );
};

const FavoritesList = ({ listId, list = [], className = '', readOnly = false }) => {
  const dispatch = useDispatch();
  const { searchData } = useSelector(state => state.app);
  const [tree, setTree] = useState(convertToTreeData(list));

  const handleRemove = pageName => {
    const removeActions = () => {
      const data = removeFromSimple(convertToSimple(tree), pageName);
      dispatch(updateFavorites(listId, { list: data }));
      setTree(convertToTreeData(data));
    };

    if (
      tree.items[pageName] &&
      tree.items[pageName].hasChildren &&
      !tree.items[pageName].children.find(item => /^empty/.test(item))
    ) {
      modal.confirm({
        message: `This action will also remove ${tree.items[pageName].children.join(', ')}. Is this okay?`,
        callback: () => removeActions(),
      });
    } else {
      removeActions();
    }
  };

  const renameFolder = (oldName, newName) => {
    modal.close();

    // Check for existing folder
    if (tree.items[newName] && tree.items[newName].hasChildren) {
      toastr.error('Error', 'Folder already exists');
      return;
    }

    const data = renameFromSimple(convertToSimple(tree), oldName, newName);

    dispatch(updateFavorites(listId, { list: data }));
    setTree(convertToTreeData(data));
  };

  const handleRename = folderName => {
    modal.open('renameFolder', {
      size: 'sm',
      title: 'Rename Folder',
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      message: (
        <NewFolder
          initialValue={folderName}
          onCancel={modal.close}
          onSubmit={payload => renameFolder(folderName, payload.folder)}
        />
      ),
    });
  };

  const renderItem = ({ item, onExpand, onCollapse, provided }) => {
    const pageData = searchData[item.data.title] || {};
    const linkType = getPageType(pageData.type);
    const isFolder = item.hasChildren;
    const isEmpty = /^empty/.test(item.id);
    const icon = isFolder ? 'folder' : linkType.icon;
    const onClickFolder = item.isExpanded ? () => onCollapse(item.id) : () => onExpand(item.id);
    const onClickLink = () => browserHistory.push(`/${item.data.title}`);

    const menuItem = menuDefs[whichMenuItem(`/${item.data.title}`)] || {};
    const itemClassName = classNames({
      empty: isEmpty,
      folder: isFolder,
    });

    if (isEmpty) {
      const { style } = provided.draggableProps;
      return (
        <FavoritesItem className={itemClassName} style={style} ref={provided.innerRef} {...provided.dragHandleProps}>
          <span className="favorite-text">Empty</span>
        </FavoritesItem>
      );
    }

    return (
      <FavoritesItem
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={itemClassName}
        onClick={isFolder ? onClickFolder : onClickLink}
      >
        <span>{getIcon(item, onExpand, onCollapse, icon, menuItem.color)}</span>
        <span className="favorite-text">
          {item.data ? item.data.title : ''}
          {pageData.title && <span className="menu-title">{pageData.title}</span>}
          {pageData.description && <span className="menu-description">{pageData.description}</span>}
        </span>
        {readOnly === false && (
          <div className="actions">
            {isFolder === true && (
              <div
                onClick={event => {
                  event.stopPropagation();
                  handleRename(item.id);
                }}
              >
                <i className="zmdi zmdi-edit zmdi-hc-fw zmdi-hc-lg" />
              </div>
            )}
            <div
              onClick={event => {
                event.stopPropagation();
                handleRemove(item.id);
              }}
            >
              <i className="zmdi zmdi-close zmdi-hc-fw zmdi-hc-lg" />
            </div>
          </div>
        )}
      </FavoritesItem>
    );
  };

  renderItem.propTypes = {
    item: PropTypes.object,
    onCollapse: PropTypes.func,
    onExpand: PropTypes.func,
    provided: PropTypes.object,
  };

  const onExpand = itemId => {
    const newTree = mutateTree(tree, itemId, { isExpanded: true });
    setTree(newTree);
    // dispatch(updateFavorites(listId, { list: convertToSimple(newTree) }));
    dispatch(updateFavoriteList(listId, convertToSimple(newTree)));
  };

  const onCollapse = itemId => {
    const newTree = mutateTree(tree, itemId, { isExpanded: false });
    setTree(newTree);
    // dispatch(updateFavorites(listId, { list: convertToSimple(newTree) }));
    dispatch(updateFavoriteList(listId, convertToSimple(newTree)));
  };

  const onDragEnd = (source, destination) => {
    if (!destination) {
      return;
    }

    const newTree = moveItemOnTree(tree, source, destination);
    if (source.parentId !== destination.parentId && newTree.items[source.parentId].children.length === 0) {
      const emptyId = `empty-${source.parentId}`;
      newTree.items[source.parentId].children[0] = emptyId;
      newTree.items[source.parentId].isExpanded = true;
      newTree.items[source.parentId].hasChildren = true;
      newTree.items[emptyId] = {
        id: emptyId,
        data: {
          title: 'Empty',
        },
        children: [],
        hasChildren: false,
        isExpanded: false,
      };
    }

    if (newTree.items[destination.parentId].children.length > 1) {
      newTree.items[destination.parentId].children = newTree.items[destination.parentId].children.filter(
        item => !/^empty/.test(item)
      );
    }

    setTree(newTree);
    dispatch(updateFavorites(listId, { list: convertToSimple(newTree) }));
  };

  useEffect(() => {
    setTree(convertToTreeData(list));
  }, [list]);

  return (
    <div className={classNames('favorites-list', className)}>
      {!list.length ? (
        <FileNotFound style={{ alignSelf: 'center' }}>
          Look for the <i className="zmdi zmdi-star zmdi-hc-fw" /> to add favorites
        </FileNotFound>
      ) : (
        <Tree
          tree={tree}
          renderItem={renderItem}
          onExpand={onExpand}
          onCollapse={onCollapse}
          onDragEnd={onDragEnd}
          offsetPerLevel={30}
          isDragEnabled={!readOnly}
        />
      )}
    </div>
  );
};

FavoritesList.propTypes = {
  className: PropTypes.string,
  list: PropTypes.array,
  listId: PropTypes.number,
  readOnly: PropTypes.bool,
};

export default FavoritesList;
